import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Avatar,
  Typography,
  Chip,
  IconButton,
  Box,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { Person } from '../../types';
import { SortOption } from '../../pages/People';
import { format } from 'date-fns';

/**
 * PeopleTable Component
 *
 * Displays people in a table layout with sortable columns
 *
 * Features:
 * - Columns: Photo, Name, Born, Died, Status, Actions
 * - Sortable columns
 * - Click row to view person
 *
 * @component
 * @param {PeopleTableProps} props - Component props
 * @returns {JSX.Element} Table view of people
 */
interface PeopleTableProps {
  persons: Person[];
  onPersonClick: (personId: string) => void;
  onSortChange: (sort: SortOption) => void;
  currentSort: SortOption;
}

export const PeopleTable: React.FC<PeopleTableProps> = ({
  persons,
  onPersonClick,
  onSortChange,
  currentSort,
}) => {
  // ===== Helper Functions =====
  const getFullName = (person: Person): string => {
    let name = `${person.firstName} ${person.lastName}`;
    if (person.middleName) {
      name = `${person.firstName} ${person.middleName} ${person.lastName}`;
    }
    if (person.suffix) {
      name += ` ${person.suffix}`;
    }
    return name;
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '—';
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch {
      return dateString;
    }
  };

  const getInitials = (person: Person): string => {
    return `${person.firstName.charAt(0)}${person.lastName.charAt(0)}`.toUpperCase();
  };

  // ===== Event Handlers =====
  const handleSort = (column: 'name' | 'birthDate') => {
    if (column === 'name') {
      if (currentSort === 'nameAsc') {
        onSortChange('nameDesc');
      } else {
        onSortChange('nameAsc');
      }
    } else if (column === 'birthDate') {
      if (currentSort === 'birthDateAsc') {
        onSortChange('birthDateDesc');
      } else {
        onSortChange('birthDateAsc');
      }
    }
  };

  const getSortDirection = (column: 'name' | 'birthDate'): 'asc' | 'desc' | undefined => {
    if (column === 'name') {
      if (currentSort === 'nameAsc') return 'asc';
      if (currentSort === 'nameDesc') return 'desc';
    } else if (column === 'birthDate') {
      if (currentSort === 'birthDateAsc') return 'asc';
      if (currentSort === 'birthDateDesc') return 'desc';
    }
    return undefined;
  };

  const isActiveSortColumn = (column: 'name' | 'birthDate'): boolean => {
    if (column === 'name') {
      return currentSort === 'nameAsc' || currentSort === 'nameDesc';
    } else if (column === 'birthDate') {
      return currentSort === 'birthDateAsc' || currentSort === 'birthDateDesc';
    }
    return false;
  };

  // ===== Render =====
  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow>
            <TableCell width={60}></TableCell>
            <TableCell>
              <TableSortLabel
                active={isActiveSortColumn('name')}
                direction={getSortDirection('name')}
                onClick={() => handleSort('name')}
              >
                Name
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={isActiveSortColumn('birthDate')}
                direction={getSortDirection('birthDate')}
                onClick={() => handleSort('birthDate')}
              >
                Born
              </TableSortLabel>
            </TableCell>
            <TableCell>Died</TableCell>
            <TableCell>Gender</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {persons.map((person) => (
            <TableRow
              key={person.id}
              hover
              onClick={() => onPersonClick(person.id)}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                }
              }}
            >
              <TableCell>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: person.gender === 'male' ? 'primary.main' :
                             person.gender === 'female' ? 'secondary.main' :
                             'grey.500'
                  }}
                >
                  <Typography variant="caption">
                    {getInitials(person)}
                  </Typography>
                </Avatar>
              </TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {getFullName(person)}
                  </Typography>
                  {person.maidenName && (
                    <Typography variant="caption" color="text.secondary">
                      née {person.maidenName}
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {formatDate(person.birthDate)}
                </Typography>
                {person.birthPlace && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    {person.birthPlace.city ?
                      `${person.birthPlace.city}${person.birthPlace.state ? `, ${person.birthPlace.state}` : ''}` :
                      person.birthPlace.state || ''}
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {person.isDeceased ? formatDate(person.deathDate) : '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                  {person.gender || '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={person.isDeceased ? 'Deceased' : 'Living'}
                  size="small"
                  color={person.isDeceased ? 'default' : 'success'}
                  variant="outlined"
                />
              </TableCell>
              <TableCell align="right">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPersonClick(person.id);
                  }}
                  title="View Profile"
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PeopleTable;
